/**
 * Product Data Service – save/load extracts and fetch product data.
 * Extracted data is stored in Firebase Realtime Database (user-scoped).
 * Fetch uses Amazon Product Database Query API when API keys are provided.
 */

import firebase from "../components/firebase";
import { base } from "../components/firebase";
import {
  ProductDataExtract,
  ProductDataFilters,
  ProductDataRow,
} from "../components/ProductDataService/types";
import {
  productDatabaseQuery,
  mapProductDatabaseResultToRow,
  ProductDatabaseQueryAttributes,
} from "./jungleScout";

const COLLECTION = "productDataExtracts";

function getUserRef() {
  const user = firebase.getCurrentUser();
  if (!user?.uid) throw new Error("Not authenticated");
  return firebase.db.ref(`${base}/${user.uid}/${COLLECTION}`);
}

/** Remove undefined values so Firebase accepts the payload (Firebase rejects undefined). */
function stripUndefined<T>(obj: T): T {
  if (obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map((item) => stripUndefined(item)) as T;
  if (obj !== null && typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== undefined) out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return obj;
}

/** Save a new extract (filters + products) and return its id */
export async function saveProductDataExtract(
  filters: ProductDataFilters,
  products: ProductDataRow[]
): Promise<string> {
  const ref = getUserRef();
  const payload = stripUndefined({
    filters,
    products,
    createdAt: new Date().toISOString(),
    marketplace: filters.marketplace,
  });
  const pushRef = ref.push(payload);
  await pushRef;
  return pushRef.key as string;
}

/** Ensure products is always an array (Firebase may return object with numeric keys). */
function normalizeProducts(products: ProductDataRow[] | Record<string, ProductDataRow> | undefined): ProductDataRow[] {
  if (!products) return [];
  if (Array.isArray(products)) return products;
  return Object.values(products);
}

/** Load all saved extracts for the current user */
export async function getProductDataExtracts(): Promise<ProductDataExtract[]> {
  const ref = getUserRef();
  const snapshot = await ref.once("value");
  const val = snapshot.val();
  if (!val) return [];
  return Object.entries(val).map(([id, v]) => {
    const item = v as Omit<ProductDataExtract, "id">;
    return {
      id,
      ...item,
      products: normalizeProducts(item.products),
    };
  });
}

/** Load a single extract by id */
export async function getProductDataExtractById(
  id: string
): Promise<ProductDataExtract | null> {
  const ref = getUserRef().child(id);
  const snapshot = await ref.once("value");
  const val = snapshot.val();
  if (!val) return null;
  return {
    id,
    ...val,
    products: normalizeProducts(val.products),
  };
}

/** Delete an extract */
export async function deleteProductDataExtract(id: string): Promise<void> {
  const ref = getUserRef().child(id);
  await ref.remove();
}

/**
 * Build Amazon Product Database Query attributes from our filters.
 */
function filtersToQueryAttributes(filters: ProductDataFilters): ProductDatabaseQueryAttributes {
  const include_keywords = filters.keywords
    ? filters.keywords
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    : undefined;
  const categories = filters.categories?.length ? filters.categories : undefined;
  const seller_types =
    filters.sellerTypes?.length && filters.sellerTypes.length < 3
      ? filters.sellerTypes.map((s) => s.toLowerCase())
      : undefined;

  return {
    include_keywords: include_keywords?.length ? include_keywords : undefined,
    categories,
    exclude_unavailable_products: true,
    min_price: filters.priceMin,
    max_price: filters.priceMax,
    min_rank: filters.categoryRankMin,
    max_rank: filters.categoryRankMax,
    min_sales: filters.salesMin,
    max_sales: filters.salesMax,
    min_reviews: filters.reviewsMin,
    max_reviews: filters.reviewsMax,
    seller_types,
  };
}

/**
 * Fetch product data via Amazon Product Database Query API.
 * Requires apiKeyName and apiKey. Throws if keys are missing or API fails.
 */
export async function fetchProductData(
  filters: ProductDataFilters,
  apiKeyName: string,
  apiKey: string
): Promise<ProductDataRow[]> {
  const marketplace = filters.marketplace === "USA" ? "us" : filters.marketplace;
  const attributes = filtersToQueryAttributes(filters);

  const response = await productDatabaseQuery(
    apiKeyName,
    apiKey,
    marketplace,
    attributes,
    100
  );

  const data = response.data ?? [];
  return data.map((row) => mapProductDatabaseResultToRow(row, marketplace));
}
